import { css, cx } from "@emotion/css";
import { ChevronLeftIcon, ChevronRightIcon } from "@octopusdeploy/design-system-icons";
import { space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import { Button } from "../Button";
import { SimpleMenu, useMenuState } from "../Menu";
import type { SimpleMenuItem } from "../MenuItems";
type TotalResultsProps = {
    /**
     * The total results that have been paginated.
     */
    totalResults: "unknown";
    /**
     * Whether there are more pages to show.
     */
    hasMoreResults: boolean;
} | {
    /**
     * The total results that have been paginated.
     */
    totalResults: number;
};
export type PaginationProps = {
    /**
     * The label for the pagination controls.
     */
    label: string;
    /**
     * The current page being displayed.
     */
    currentPage: number;
    /**
     * The range of options to present for the number of items per page.
     */
    itemsPerPageOptions: readonly number[];
    /**
     * The selected number of items per page.
     */
    selectedItemsPerPage: number;
    /**
     * The callback to run when the page is changed.
     */
    onPageChange: (page: number) => void;
    /**
     * The callback to run when the page size is changed.
     */
    onPageSizeChange: (pageSize: number) => void;
} & TotalResultsProps;
export function Pagination({ label, currentPage, itemsPerPageOptions, selectedItemsPerPage, onPageChange, onPageSizeChange, ...props }: PaginationProps) {
    const [openMenu, menuState, buttonAriaAttributes] = useMenuState();
    const hasTotalResults = props.totalResults !== "unknown";
    const pageStart = Math.min((currentPage - 1) * selectedItemsPerPage + 1, hasTotalResults ? props.totalResults : Infinity);
    const pageEnd = Math.min(currentPage * selectedItemsPerPage, hasTotalResults ? props.totalResults : Infinity);
    const totalResultsText = hasTotalResults ? `${pageStart} - ${pageEnd} of ${props.totalResults} ${label}` : `${pageStart} - ${pageEnd} ${label}`;
    const previousIsDisabled = currentPage === 1;
    const nextIsDisabled = hasTotalResults ? currentPage * selectedItemsPerPage >= props.totalResults : !props.hasMoreResults;
    const hasPageSizeOptions = itemsPerPageOptions.length > 0;
    const defaultValue = itemsPerPageOptions[0];
    const pageSizeIsDisabled = !hasPageSizeOptions || (hasTotalResults ? props.totalResults <= defaultValue : true);
    const pagingButtonsAreDisabled = previousIsDisabled && nextIsDisabled;
    const menuItems: SimpleMenuItem[] = [];
    menuItems.push({
        type: "information",
        content: <p className={resultsPerPageMenuItemStyles}>{label} per page</p>,
    });
    itemsPerPageOptions.map((itemsPerPage) => {
        menuItems.push({
            type: "button",
            label: `${itemsPerPage}`,
            onClick: () => onPageSizeChange(itemsPerPage),
            isSelected: itemsPerPage === selectedItemsPerPage,
        });
    });
    return (<div className={paginationWrapper}>
            <div className="tracker-pagination">
                {!hasTotalResults || pageSizeIsDisabled ? (<p className={cx(textOnlyResultsPerPageStyles, { [disabledTextOnlyResultsPerPageStyles]: pagingButtonsAreDisabled })}>{totalResultsText}</p>) : (<>
                        <Button importance="quiet" {...buttonAriaAttributes} showExpandCollapseIcon={false} disabled={pageSizeIsDisabled} accessibleName={totalResultsText} label={totalResultsText} onClick={openMenu}/>
                        {hasTotalResults && menuState.isOpen && <SimpleMenu compact={true} menuState={menuState} items={menuItems} accessibleName={"Change page size"}/>}
                    </>)}
            </div>
            <div className={iconButtonWrapper}>
                <Button onClick={() => onPageChange(currentPage - 1)} importance="ghost" accessibleName="Previous" disabled={previousIsDisabled} icon={<ChevronLeftIcon size={20}/>}/>
            </div>
            <div className={iconButtonWrapper}>
                <Button onClick={() => onPageChange(currentPage + 1)} importance="ghost" accessibleName="Next" disabled={nextIsDisabled} icon={<ChevronRightIcon size={20}/>}/>
            </div>
        </div>);
}
const resultsPerPageMenuItemStyles = css({
    font: text.body.bold.medium,
    margin: 0,
});
const textOnlyResultsPerPageStyles = css({
    font: text.body.bold.medium,
    color: themeTokens.color.text.info,
    margin: 0,
    width: "max-content",
});
const disabledTextOnlyResultsPerPageStyles = css({
    color: themeTokens.color.text.tertiary,
});
const paginationWrapper = css({
    display: "flex",
    alignItems: "center",
    gap: space[2],
});
const iconButtonWrapper = css({
    color: themeTokens.color.button.text.quiet,
});
